/**
 * AI-OS API client — Slice 8, Gate 2.
 *
 * Thin fetchers over the read-only /ai/brain/* routes (Gate-1 seams +
 * Gate-2 projections). No caching, no transformation, no mock fallbacks —
 * screens render exactly what the seams return, or an honest error state.
 * companyId is NEVER sent from the client: the backend derives it from the
 * auth token only (frozen tenant-isolation invariant).
 */
import api from "../services/api";

const BASE = "/ai/brain";

const get = async path => {
  const { data } = await api.get(`${BASE}${path}`);
  return data;
};

// Mutating counterpart to the read-only seams — the one sanctioned lifecycle
// transition (human approve/reject). companyId + actor still come from the
// auth token server-side; the body carries only the resolution + reason.
const post = async (path, body) => {
  const { data } = await api.post(`${BASE}${path}`, body);
  return data;
};

// Gate-1 seams
export const fetchBrainOverview = () => get("/overview");
/**
 * Explicit page size rather than the seam's silent default. candidateQueue clamps to 200
 * and falls back to 50 when nothing is passed, and applies no status filter unless one is
 * given — so this is the oldest 50 across ALL four statuses, and the card says so.
 */
export const fetchCandidates = () => get("/candidates?limit=50");
export const resolveCandidate = (candidateId, resolution, reason) =>
  post(`/candidates/${candidateId}/resolve`, { resolution, reason });
export const fetchOrgMemory = () => get("/org-memory");
export const fetchObservationHealth = () => get("/observations/health");
// order=desc is the Gate-2 additive seam extension (read-only) — the log
// screen shows the NEWEST decisions; without it the seam returns the oldest
// page and the card would present stale rows as current.
export const fetchDecisions = () => get("/decisions?order=desc&limit=50");
// order=desc is the Gate-3 additive seam extension (same class as
// /decisions): the audit cards show the NEWEST approvals, not the oldest page.
export const fetchApprovals = () => get("/approvals?order=desc&limit=20");
export const fetchPolicies = () => get("/policies");
export const fetchConfigVersions = () => get("/config-versions");
export const fetchFlags = () => get("/flags");
export const fetchGovernanceMetrics = () => get("/governance-metrics");

// Gate-2 projections
export const fetchCalibration = () => get("/calibration");
export const fetchWorkforce = () => get("/workforce");
export const fetchKnowledge = () => get("/knowledge");
/**
 * What the knowledge resolver decided — per-source admissions and rejections with their
 * freshness and reason, and same-fact conflicts. Computed on every governed turn; this is
 * the read-only projection of it, not a second computation.
 */
export const fetchKnowledgeProvenance = () => get("/knowledge-provenance");
export const fetchDiscoveryPrefill = () => get("/discovery-prefill");

// Gate-3 projections (contextual brain / previews / diagnostics)
export const fetchContactContext = contactId =>
  get(`/context/contact/${encodeURIComponent(contactId)}`);
export const fetchAudiencePreview = () => get("/audience-preview");
export const fetchQueuesFlow = () => get("/queues-flow");
export const fetchDiagnostics = () => get("/diagnostics");
